import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Coffee, ArrowRight, Sparkles } from 'lucide-react';
import api from "../../api/axios";
import { stripHtml, truncate } from "../../utils/text";
import { useLanguage } from "../../context/LanguageContext";

export default function MorningBrew({ user }) {
    const { t } = useLanguage();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // In a real app, this would fetch based on users' mood or a daily rotation ID from backend
        // For now, we'll fetch a few explore articles and pick one pseudo-randomly for the day
        const fetchDaily = async () => {
            try {
                const res = await api.get("/articles");

                const data = res.data;
                if (data.length > 0) {
                    // Simple daily rotation based on date
                    const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
                    const index = dayOfYear % data.length;
                    setArticle(data[index]);
                }
            } catch (e) {
                console.error("Failed to brew morning wisdom", e);
            } finally {
                setLoading(false);
            }
        };

        fetchDaily();
    }, []);

    if (loading) return null; // Don't show anything while loading to avoid layout shift, or show skeleton
    if (!article) return null;

    return (
        <div className="mb-10 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-800 dark:to-gray-800 rounded-2xl p-1 border border-orange-100 dark:border-gray-700 shadow-sm relative overflow-hidden">
            {/* Decorative bg elements */}
            <div className="absolute top-0 right-0 p-8 opacity-10">
                <Coffee className="w-32 h-32 text-orange-500" />
            </div>

            <div className="relative bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm rounded-xl p-6 md:p-8 flex flex-col md:flex-row gap-8 items-center">

                {/* Image Section */}
                <div className="w-full md:w-1/3 flex-shrink-0">
                    <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-md bg-gray-200">
                        {article.coverImage ? (
                            <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-orange-100 text-orange-400">
                                <Sparkles className="w-12 h-12" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Content Section */}
                <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-2 text-orange-600 mb-3 font-medium uppercase tracking-wider text-xs">
                        <Coffee className="w-4 h-4" />
                        <span>{t('brew_title')}</span>
                    </div>

                    <h2 className="text-2xl md:text-3xl font-display font-bold text-gray-900 dark:text-gray-100 mb-3 leading-tight">
                        {article.title}
                    </h2>

                    <p className="text-gray-600 dark:text-gray-300 mb-6 line-clamp-2 md:line-clamp-3 leading-relaxed">
                        {truncate(stripHtml(article.snippet || article.content), 150)}
                    </p>

                    <div className="flex flex-col md:flex-row items-center gap-4">
                        <Link
                            to={`/article/${article.id}`}
                            className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-6 py-2.5 rounded-full font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-all flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                            {t('brew_read_article')} <ArrowRight className="w-4 h-4" />
                        </Link>
                        <span className="text-sm text-gray-400">
                            {t('brew_read_time', { time: article.readTime || "5 min" })}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
